/**
 * The Output section's best-effort preflight lines (spec §12.3), computed on
 * the client from layer metadata alone. A line whose inputs are unknown is
 * left out rather than guessed.
 *
 * Sources:
 *   - extents: `layer.bboxNew.coords` (`[[minX, minY, minZ], [maxX, maxY, maxZ]]`,
 *     project CRS — the 3D bbox `LayerProperties` / `RunRainfallAnalysisDialog`
 *     read too);
 *   - point count: `metadata.potreeMetadata.points` (shown in `LayerProperties`).
 */
import type { CompareFormValues } from './formSchema';

/** `surface_difference.py`'s `MAX_CELLS`: above it the worker coarsens the
 *  grid to √(area / MAX_CELLS). */
export const MAX_CELLS = 25_000_000;
/** Above this many points (after decimation) the line suggests Decimate. */
export const DECIMATE_TIP_POINTS = 50_000_000;
const COARSEN_STEP_M = 0.05;

export interface PreflightLine {
  key: string;
  params: Record<string, string | number>;
  tone: 'muted' | 'warning';
}

/** The layer fields the estimate reads. */
export interface PreflightLayer {
  id: string;
  type?: string | null;
  metadata?: unknown;
  bboxNew?: unknown;
}

/** The alignment fields the corridor reads: `Alignment.points`, flattened
 *  world XYZ. */
export interface PreflightAlignment {
  points: ArrayLike<number>;
}

type Rect = { minX: number; minY: number; maxX: number; maxY: number };

/** A bbox whose every coordinate fits in ±180 reads as degrees (EPSG:4326),
 *  not metres: its area would be meaningless, so it counts as unknown. */
const looksGeographic = (r: Rect): boolean =>
  [r.minX, r.minY, r.maxX, r.maxY].every((c) => Math.abs(c) <= 180);

const rectOf = (layer: PreflightLayer | undefined): Rect | null => {
  const coords = (layer?.bboxNew as { coords?: unknown } | null | undefined)?.coords;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const [lo, hi] = coords as unknown[][];
  if (!Array.isArray(lo) || !Array.isArray(hi)) return null;
  const [minX, minY] = lo as number[];
  const [maxX, maxY] = hi as number[];
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  const rect = { minX, minY, maxX, maxY };
  return looksGeographic(rect) ? null : rect;
};

/** The station corridor `surface_difference.py` narrows the grid to: the
 *  centreline's XY bbox padded by max(swath) + one station interval
 *  (`pad = max(swathLeft, swathRight) + stationInterval`, then
 *  `station_bins.corridor_bbox`). */
const corridorOf = (points: ArrayLike<number>, pad: number): Rect | null => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < points.length; i += 3) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minY = Math.min(minY, points[i + 1]);
    maxY = Math.max(maxY, points[i + 1]);
  }
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
};

const intersect = (a: Rect, b: Rect): Rect => ({
  minX: Math.max(a.minX, b.minX),
  minY: Math.max(a.minY, b.minY),
  maxX: Math.min(a.maxX, b.maxX),
  maxY: Math.min(a.maxY, b.maxY),
});

/** "3.1" / "16" / "182" — the number before the locale string's "M". Never
 *  "0.0": anything under 0.05 M reads "<0.1". */
const millions = (n: number): string => {
  const m = n / 1_000_000;
  if (m < 0.05) return '<0.1';
  return m < 10 ? m.toFixed(1) : String(Math.round(m));
};

/** Up to the next 0.05 m. The epsilon keeps an exact multiple (√0.16 = 0.4)
 *  from being pushed a step up by float error (0.4 / 0.05 = 8.000…02). */
const roundUpToStep = (x: number): number =>
  Math.round(Math.ceil(x / COARSEN_STEP_M - 1e-9) * COARSEN_STEP_M * 100) / 100;

/** The grid the worker will use: the overlap of both inputs, narrowed by the
 *  boundary's bbox and, with Volumes by chainage on, the station corridor (as
 *  `make_grid` does). `null` when it cannot be known. */
function cutFillLine(
  v: CompareFormValues,
  layers: readonly PreflightLayer[],
  alignment: PreflightAlignment | undefined,
): PreflightLine | null {
  const byId = (id: string) => (id ? layers.find((l) => l.id === id) : undefined);
  const top = rectOf(byId(v.inputs.topLayerId));
  const bottom = rectOf(byId(v.inputs.bottomLayerId));
  const boundary = rectOf(byId(v.inputs.boundaryLayerId));
  const corridor =
    v.stationVolumesEnabled && alignment
      ? corridorOf(alignment.points, Math.max(v.swathLeft, v.swathRight) + v.stationInterval)
      : null;
  // A narrowing input with no known extent would make this an upper bound
  // that can raise a false "will be coarsened" — better no line.
  if (!top || !bottom) return null;
  if (v.inputs.boundaryLayerId && !boundary) return null;
  if (v.stationVolumesEnabled && !corridor) return null;
  let area = intersect(top, bottom);
  if (boundary) area = intersect(area, boundary);
  if (corridor) area = intersect(area, corridor);
  const areaM2 = Math.max(0, area.maxX - area.minX) * Math.max(0, area.maxY - area.minY);
  const grid = v.cutFill.resolution;
  if (!(areaM2 > 0) || !(grid > 0)) return null;
  const cells = areaM2 / (grid * grid);
  if (cells > MAX_CELLS) {
    return {
      key: 'compareSurfaces.preflight.coarsened',
      params: { grid: roundUpToStep(Math.sqrt(areaM2 / MAX_CELLS)) },
      tone: 'warning',
    };
  }
  return { key: 'compareSurfaces.preflight.cells', params: { cells: millions(cells), grid }, tone: 'muted' };
}

function deviationLine(v: CompareFormValues, layers: readonly PreflightLayer[]): PreflightLine | null {
  const cloud = layers.find((l) => l.id === v.inputs.bottomLayerId);
  const points = (cloud?.metadata as { potreeMetadata?: { points?: unknown } } | null | undefined)?.potreeMetadata
    ?.points;
  if (typeof points !== 'number' || !(points > 0)) return null;
  const decimate = v.deviation.decimate >= 1 ? v.deviation.decimate : 1;
  const kept = points / decimate;
  return {
    key:
      decimate === 1 && kept >= DECIMATE_TIP_POINTS
        ? 'compareSurfaces.preflight.pointsDecimateTip'
        : 'compareSurfaces.preflight.points',
    params: { points: millions(kept) },
    tone: 'muted',
  };
}

/**
 * The preflight lines for the ticked results, in display order.
 * `stationAlignment` is the alignment Volumes by chainage will send (the
 * dialog's `alignments.get(v.alignmentKey)`); while the section is on and it
 * is unknown, the cells line is left out.
 */
export function preflightLines(
  v: CompareFormValues,
  layers: readonly PreflightLayer[],
  stationAlignment?: PreflightAlignment,
): PreflightLine[] {
  const cutFill = v.results.includes('cutFill');
  const deviation = v.results.includes('deviation');
  const lines: (PreflightLine | null)[] = [
    cutFill ? cutFillLine(v, layers, stationAlignment) : null,
    deviation ? deviationLine(v, layers) : null,
    cutFill && deviation ? { key: 'compareSurfaces.preflight.startsTwoJobs', params: {}, tone: 'muted' } : null,
  ];
  return lines.filter((l): l is PreflightLine => l !== null);
}

/**
 * A preflight line's numbers in the reader's locale ("3,1", "0,25" in
 * German). The model hands over plain numbers and "3.1" / "<0.1" strings; a
 * string keeps its own number of decimals ("4.0" stays "4,0").
 */
export function localizePreflightParams(params: PreflightLine['params'], language: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'number') {
      out[key] = new Intl.NumberFormat(language, { maximumFractionDigits: 3 }).format(value);
      continue;
    }
    const m = /^(<?)(\d+)(?:\.(\d+))?$/.exec(value);
    out[key] = m
      ? m[1] +
        new Intl.NumberFormat(language, {
          minimumFractionDigits: m[3]?.length ?? 0,
          maximumFractionDigits: m[3]?.length ?? 0,
        }).format(Number(`${m[2]}.${m[3] ?? '0'}`))
      : value;
  }
  return out;
}
